import React, { useState, useRef } from 'react';
import { Upload, FileText, X, TrendingUp, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import { isValidFileType, getFileInputAccept, getFileTypeInfo } from '../utils/fileUpload';

interface SalesDataUploadProps {
    databaseId?: number | string | null;
    onUploadSuccess?: () => void;
}

interface UploadResult {
    message: string;
    created?: number;
    updated?: number;
    total_rows?: number;
    errors_count?: number;
    errors?: string[];
    additional_errors?: number;
    prediction_update_warning?: string;
}

interface UploadResultModalProps {
    isOpen: boolean;
    onClose: () => void;
    result: UploadResult;
}

const UploadResultModal: React.FC<UploadResultModalProps> = ({ isOpen, onClose, result }) => {
    if (!isOpen) return null;

    const hasErrors = (result.errors_count || 0) > 0;
    const hasWarnings = result.errors && result.errors.length > 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-2xl mx-4 max-h-[80vh] overflow-y-auto w-full">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${hasErrors ? 'bg-amber-100' : 'bg-blue-100'}`}>
                            {hasErrors ? (
                                <AlertTriangle className={`${hasErrors ? 'text-amber-600' : 'text-blue-600'}`} size={20} />
                            ) : (
                                <CheckCircle className="text-blue-600" size={20} />
                            )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Résultat de l'import des ventes
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <div className="space-y-4">
                    {/* Summary Statistics */}
                    <div className={`p-4 rounded-lg border ${hasErrors ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                        <h4 className={`font-medium mb-3 ${hasErrors ? 'text-amber-900' : 'text-blue-900'}`}>
                            {hasErrors ? 'Import terminé avec des erreurs' : 'Import terminé avec succès'}
                        </h4>
                        {(result.created !== undefined || result.updated !== undefined) && (
                            <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                                {result.created !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-blue-800'}>
                                        <span className="font-medium">Entrées créées:</span> {result.created}
                                    </div>
                                )}
                                {result.updated !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-blue-800'}>
                                        <span className="font-medium">Entrées mises à jour:</span> {result.updated}
                                    </div>
                                )}
                                {result.total_rows !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-blue-800'}>
                                        <span className="font-medium">Total lignes:</span> {result.total_rows}
                                    </div>
                                )}
                                {result.errors_count !== undefined && (
                                    <div className={hasErrors ? 'text-amber-800' : 'text-blue-800'}>
                                        <span className="font-medium">Erreurs:</span> {result.errors_count}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className={`p-2 bg-white bg-opacity-50 rounded border text-xs ${hasErrors ? 'text-amber-800' : 'text-blue-800'}`}>
                            <strong>Résultat:</strong> {result.message}
                        </div>
                    </div>

                    {/* Prediction Update Warning */}
                    {result.prediction_update_warning && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                                <AlertTriangle className="text-amber-600" size={16} />
                                Note importante
                            </h4>
                            <p className="text-sm text-amber-800">
                                {result.prediction_update_warning}
                            </p>
                        </div>
                    )}

                    {/* Error Details */}
                    {hasWarnings && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <h4 className="font-medium text-red-900 mb-3 flex items-center gap-2">
                                <AlertTriangle size={16} />
                                Détails des erreurs
                            </h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {result.errors!.map((error, index) => (
                                    <div key={index} className="text-sm text-red-800 p-2 bg-white bg-opacity-50 rounded border">
                                        {error}
                                    </div>
                                ))}
                            </div>
                            
                            {result.additional_errors && result.additional_errors > 0 && (
                                <div className="mt-3 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                                    <strong>Note:</strong> {result.additional_errors} erreur(s) supplémentaire(s) non affichée(s). 
                                    Consultez les logs du serveur pour la liste complète.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Success Message */}
                    {!hasErrors && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-800 text-sm">
                                🎉 Les données de ventes ont été importées avec succès ! 
                                Les prévisions seront mises à jour automatiquement.
                            </p>
                        </div>
                    )}
                </div>
                
                <div className="flex justify-end mt-6">
                    <button
                        onClick={onClose}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                            hasErrors 
                                ? 'bg-amber-600 text-white hover:bg-amber-700' 
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
};

interface FormatModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const FormatModal: React.FC<FormatModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-3xl mx-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Format fichier Ventes</h3>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Fichiers acceptés</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                            <li><strong>Formats:</strong> {getFileTypeInfo().formats}</li>
                            <li><strong>Encodages CSV:</strong> utf-8, latin1, cp1252 (détection automatique)</li>
                            <li><strong>Délimiteurs CSV:</strong> ; , tabulation, | (détection automatique)</li>
                            <li><strong>Excel multi-feuilles:</strong> si plusieurs onglets, la première feuille est lue</li>
                            <li><strong>Taille conseillée:</strong> ≤ 50 Mo</li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Colonnes requises</h4>
                        <div className="text-sm text-gray-700 space-y-2">
                            <div>
                                <div className="font-medium">Identifiant produit</div>
                                <ul className="list-disc ml-5 mt-1 space-y-1">
                                    <li><strong>La première colonne non-vente</strong> est utilisée comme identifiant produit.</li>
                                    <li>Recommandé: placez une colonne ID avec un en-tête courant: <span className="font-mono text-xs">ref, id, item_code, product_id, article_id, sku</span>.</li>
                                    <li>Normalisation appliquée: caractères non alphanumériques supprimés; les nombres (ex: 101.0) deviennent des entiers ("101").</li>
                                    <li>Doit <strong>correspondre à un article existant</strong> de la base (colonne <span className="font-mono">ref</span>). Les références inconnues sont <strong>ignorées</strong>.</li>
                                </ul>
                            </div>
                            <div>
                                <div className="font-medium mt-3">Colonnes de ventes</div>
                                <ul className="list-disc ml-5 mt-1 space-y-1">
                                    <li>En-têtes reconnus: <strong>MMM_AA</strong> (ex: <span className="font-mono">janv_22</span>).</li>
                                    <li>Mois acceptés: <span className="font-mono">janv, fevr, mars, avr, mai, juin, juil, aout, sept, oct, nov, dec</span>.</li>
                                    <li>Année: 2 chiffres (ex: 22, 23...).</li>
                                    <li>Entêtes date comme <span className="font-mono">2022-01</span>, <span className="font-mono">01/2022</span> sont automatiquement convertis en <span className="font-mono">MMM_AA</span>.</li>
                                    <li>Valeurs attendues: quantités numériques (converties en entiers; vides ou non numériques → 0).</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Comportement d'écrasement</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                            <li>Pour chaque référence reconnue, <strong>toutes les ventes existantes</strong> de cette référence sont <strong>supprimées puis remplacées</strong> par celles du fichier.</li>
                            <li>Les références inconnues sont listées comme ignorées dans le résultat.</li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Exemples</h4>
                        <div className="p-3 bg-gray-50 rounded border text-xs font-mono overflow-x-auto">
                            ref,janv_22,fevr_22,mars_22<br/>
                            10160,100,120,90<br/>
                            SKU-10161,80,95,110
                        </div>
                        <div className="p-3 bg-gray-50 rounded border text-xs font-mono overflow-x-auto mt-3">
                            item_code,2022-01,2022-02,2022-03<br/>
                            A-1001,10,0,7
                        </div>
                    </div>

                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-sm text-blue-800">
                            <strong>Rappels:</strong> la colonne identifiant doit correspondre à vos articles existants. Les colonnes de ventes peuvent être nombreuses; seules celles reconnues seront importées.
                        </p>
                    </div>
                </div>
                
                <div className="flex justify-end mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Compris
                    </button>
                </div>
            </div>
        </div>
    );
};

const SalesDataUpload: React.FC<SalesDataUploadProps> = ({ databaseId, onUploadSuccess }) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [dragActive, setDragActive] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [showFormatModal, setShowFormatModal] = useState<boolean>(false);
    const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
    const [showResultModal, setShowResultModal] = useState<boolean>(false);
    const { activeDatabase } = useDatabaseContext();
    const inputRef = useRef<HTMLInputElement>(null);

    // Find the target database ID - prioritize databaseId prop over activeDatabase
    const targetDatabaseId = databaseId || activeDatabase?.id;

    // Use shared file type validation

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const selectedFile = event.target.files[0];
            if (isValidFileType(selectedFile)) {
                setFile(selectedFile);
                setError('');
                setUploadResult(null);
                setShowResultModal(false);
            } else {
                setError(getFileTypeInfo().errorMessage);
            }
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const droppedFile = e.dataTransfer.files[0];
            if (isValidFileType(droppedFile)) {
                setFile(droppedFile);
                setError('');
                setUploadResult(null);
                setShowResultModal(false);
            } else {
                setError(getFileTypeInfo().errorMessage);
            }
        }
    };

    const handleClick = () => {
        inputRef.current?.click();
    };

    const removeFile = () => {
        setFile(null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        setError('');
        setUploadResult(null);
        setShowResultModal(false);
    };

    const handleSubmitWithConfirmation = () => {
        if (!file || !targetDatabaseId) {
            setError('Veuillez sélectionner un fichier');
            return;
        }
        setShowConfirmDialog(true);
    };

    const handleCloseResultModal = () => {
        setShowResultModal(false);
        setUploadResult(null);
    };

    const handleConfirmedSubmit = async () => {
        if (!file || !targetDatabaseId) return;

        const formData = new FormData();
        formData.append('file', file);

        setLoading(true);
        setError('');
        setShowConfirmDialog(false);
        setUploadResult(null);
        setShowResultModal(false);

        try {
            const response = await api.post(API_ROUTES.DB_MANAGER.BASE + API_ROUTES.DB_MANAGER.UPLOAD_SALES_DATA(targetDatabaseId), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Assuming the API returns a result object, or create one from the response
            setUploadResult(response.data || { message: 'Données de ventes importées avec succès !' });
            setShowResultModal(true);
            setFile(null);
            
            // Call success callback if provided
            onUploadSuccess?.();
            
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            
        } catch (err: any) {
            setError(err.response?.data?.error || 'Une erreur est survenue lors de l\'import');
        } finally {
            setLoading(false);
        }
    };

    if (!targetDatabaseId) {
        return (
            <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-yellow-800 text-sm">
                    Aucune base de données sélectionnée. Veuillez sélectionner une base de données avant d'importer des données de ventes.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-xl">
                        <TrendingUp className="text-blue-600" size={24} />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Données de Ventes
                        </h2>
                        <p className="text-gray-600 text-sm">
                            Importez les données de ventes pour {activeDatabase?.name || 'votre base'}
                        </p>
                    </div>
                </div>
                
                <button
                    onClick={() => setShowFormatModal(true)}
                    className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                >
                    <Info size={16} />
                    Format Fichier
                </button>
            </div>

            {/* Drag & Drop Area - Compact */}
            <div
                className={`relative border-2 border-dashed rounded-xl p-4 transition-all cursor-pointer group ${
                    dragActive 
                        ? 'border-blue-400 bg-blue-50' 
                        : file 
                            ? 'border-green-400 bg-green-50' 
                            : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50/30'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={inputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept={getFileInputAccept()}
                    className="hidden"
                    disabled={loading}
                />
                
                {file ? (
                    // File Selected State - Horizontal Layout
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                                <FileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900 text-sm truncate">{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeFile();
                                        }}
                                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                <p className="text-gray-600 text-xs">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                            </div>
                        </div>
                        
                        {/* Import Button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSubmitWithConfirmation();
                            }}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm flex-shrink-0"
                        >
                            {loading ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Upload size={16} />
                            )}
                            {loading ? 'Import...' : 'Importer'}
                        </button>
                    </div>
                ) : (
                    // Drop Zone State - Horizontal Layout
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg transition-all flex-shrink-0 ${
                            dragActive ? 'bg-blue-200' : 'bg-gray-100 group-hover:bg-blue-100'
                        }`}>
                            <Upload className={`transition-colors ${
                                dragActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-500'
                            }`} size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 mb-1">
                                {dragActive ? 'Déposez votre fichier ici' : 'Glissez vos données de ventes'}
                            </h3>
                            <p className="text-xs text-gray-600">
                                ou <span className="text-blue-600 font-medium">cliquez pour parcourir</span>
                                {' • '}
                                <span className="text-gray-500">CSV, XLSX, XLS</span>
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 max-w-md mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <AlertTriangle className="text-blue-600" size={20} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                Confirmer l'import
                            </h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Êtes-vous sûr de vouloir importer ces données de ventes ? 
                            Cette opération enrichira votre base de données avec les nouvelles données.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleConfirmedSubmit}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Confirmer l'import
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Format Modal */}
            <FormatModal isOpen={showFormatModal} onClose={() => setShowFormatModal(false)} />

            {/* Upload Result Modal */}
            {uploadResult && (
                <UploadResultModal 
                    isOpen={showResultModal}
                    onClose={handleCloseResultModal}
                    result={uploadResult}
                />
            )}

            {/* Error Messages */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-3">
                    <X className="text-red-500 mt-0.5 flex-shrink-0" size={16} />
                    {error}
                </div>
            )}
        </div>
    );
};

export default SalesDataUpload;